import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import * as momentRef from 'moment';
import moment from 'moment-timezone';

import { DATE_FORMAT, TIME_FORMAT } from 'appConstants';
import { DatePicker, TimePicker } from 'components/Layout/Form';
import { TRewardListPage } from 'containers/Reward/i18n/TModules';

interface DateTimePickerProps {
  timezone?: string;
  value: momentRef.Moment | null;
  onChange: (date: momentRef.Moment | null | string) => void;
  showDate?: boolean;
  showTime?: boolean;
  isDisableFutureDate?: boolean;
  isDisablePastDate?: boolean;
  datePickerId?: string;
  timePickerId?: string;
  getCalendarContainer?: (triggerNode: Element) => HTMLElement;
  getPopupContainer?: (triggerNode: Element) => HTMLElement;
  disabled?: boolean;
  style?: CSSProperties;
}

const disabledPastDate = (current: momentRef.Moment) => current && current < moment().endOf('day');

const disabledFutureDate = (current: momentRef.Moment) =>
  current && current > moment().endOf('day');

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  timezone = 'UTC',
  value,
  onChange,
  showDate,
  showTime,
  isDisableFutureDate,
  isDisablePastDate,
  getCalendarContainer,
  getPopupContainer,
  disabled,
  style = {}
}) => {
  const { t } = useTranslation();

  const dateChanged = (changeValue: any, valueString: string) => {
    let newValue: any = null;
    if (!!changeValue) {
      const time = value ? moment.tz(value, timezone).format('HH:mm') : '00:00';
      newValue = valueString ? moment.tz(valueString + ' ' + time, timezone).toISOString() : '';
    }
    if (onChange) onChange(newValue);
  };

  const timeChanged = (changeValue: any, valueString: string) => {
    if (changeValue && valueString) {
      const v = moment.tz(changeValue, timezone);
      const time = moment(changeValue);
      v.set({ hour: time.get('hour'), minute: time.get('minute'), second: time.get('second') });

      if (value) {
        const m = moment.tz(value, timezone);
        v.set({
          date: m.get('date'),
          month: m.get('month'),
          year: m.get('year')
        });
      }
      if (onChange) onChange(v);
    } else if (onChange) {
      onChange(null);
    }
  };

  const dateTime = value ? moment.tz(value, timezone) : null;
  const disableDate = isDisableFutureDate
    ? disabledFutureDate
    : isDisablePastDate
    ? disabledPastDate
    : null;

  return (
    <div style={{ display: 'flex', flexFlow: 'wrap', gap: '0.5rem' }}>
      {showDate && (
        <DatePicker
          data-testid="approval-date"
          className="datePicker"
          disabledDate={disableDate}
          style={{ flex: '1 0 130px', marginRight: 10, ...style }}
          value={dateTime ? moment(dateTime.format('YYYY-MM-DD'), 'YYYY-MM-DD') : undefined}
          onChange={(v, s) => dateChanged(v, s)}
          format={DATE_FORMAT}
          getPopupContainer={getCalendarContainer}
          disabled={disabled}
          placeholder={`${t(`${TRewardListPage}.SelectDate`)} `}
        />
      )}

      {showTime && (
        <TimePicker
          data-testid="approval-time"
          className="timePicker"
          style={{ flex: '1 0 130px', marginRight: 10, ...style }}
          value={
            dateTime
              ? moment(
                  moment(new Date()).format('YYYY-MM-DD') + ' ' + dateTime.format('HH:mm'),
                  'YYYY-MM-DD HH:mm'
                )
              : undefined
          }
          onChange={(v, s) => timeChanged(v, s)}
          format={TIME_FORMAT}
          getPopupContainer={getPopupContainer}
          disabled={disabled}
          placeholder={`${t(`${TRewardListPage}.SelectTime`)} `}
        />
      )}
    </div>
  );
};

export default DateTimePicker;
